// noinspection JSAnnotator

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {ConfirmationService, FilterMetadata, LazyLoadEvent, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {TableRequest} from "../../model/api/table-request";
import {Table} from "primeng/table";
import * as XLSX from "xlsx";
import {BookType} from "xlsx";
import {CcBaseComponent} from "../cc-base.component";
import {ExportAsService} from "ngx-export-as";
import {TableColumnConfig} from "../../model/api/table-column-config";
import {DateTime} from "luxon";
import {ContextMenu} from "primeng/contextmenu";
import {AppComponent} from "../../app.component";
import {CcCellDispatcherComponent} from "../table/cell-dispatcher/cc-cell-dispatcher.component";

@Component({
  template: '',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CcVscrollTableBaseComponent extends CcBaseComponent {

  @Input()
  tableConfig: TableColumnConfig[] = [];

  @Input()
  records: any[] = [];

  @Input()
  totalRecords: number = 0;

  @Input()
  loading: boolean = false;

  @Input()
  scrollHeight: string = 'flex';

  @Input()
  currentRequest: TableRequest;

  @Output()
  loadRecordsEvent = new EventEmitter<TableRequest>();

  @Output()
  filterChangedEvent = new EventEmitter<TableRequest>();
  @Output()
  sortChangedEvent = new EventEmitter<TableRequest>();

  @Output()
  onClearFilter = new EventEmitter<TableRequest>();

  //translation part key
  @Input()
  transPartKey: String;

  //record field keys
  @Input()
  fieldId: string;

  @Input()
  fieldName: string;

  @Input()
  fieldEnabled: string;

  @Input()
  defaultSortField: string;

  @Input()
  defaultSortOrder: number = 1;

  @Input()
  disableContextMenu: boolean = false;

  @Input()
  disableSelection: boolean = false;

  @Input()
  disableFilters: boolean = false;

  globalFilterValue: string;

  @Output()
  onRowRightClick = new EventEmitter<any>();

  @Output()
  onRowDblClick = new EventEmitter<any>();

  @Output()
  onCtrlShift = new EventEmitter<any>();

  @Output()
  onShift_S = new EventEmitter<any>();

  @ViewChild('ccTable')
  ccTable: Table;

  @ViewChild('tableContextMn')
  tableContextMn: ContextMenu;

  @ViewChildren('ccCell')
  ccCells: QueryList<CcCellDispatcherComponent>;

  ctrlHitLastTime: DateTime;
  shiftHitLastTime: DateTime;
  repeatTimeDiff: number = 300;

  selectedCellValue = undefined;
  selectedCellColumnConfig = undefined;

  focused: boolean = false;
  focusedRowIndex: number = -1;


  constructor(protected el: ElementRef,
              cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService, private exportAsService: ExportAsService) {
    super(cdref, messageService, translateService);
    this.ctrlHitLastTime = DateTime.fromMillis(0);
    this.shiftHitLastTime = DateTime.fromMillis(0);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public loadRecords(event: LazyLoadEvent) {
    const newRequest = new TableRequest(event)
    this.loadRecordsFromRequest(newRequest);
  }

  public loadRecordsFromRequest(newRequest: TableRequest) {
    this.syncOriginEventFromRequest(newRequest);

    if (!this.currentRequest || !this.isSameRequest(this.currentRequest, newRequest)) {
      this.loadRecordsEvent.emit(newRequest);
    }
  }

  public setFilter(columnConfig: TableColumnConfig, filterValue: string) {
    const matchMode = columnConfig.filterConfig.matchMode ? columnConfig.filterConfig.matchMode : 'equals';

    if (!this.ccTable.isFilterBlank(filterValue)) {
      this.ccTable.filters[columnConfig.field] = [{
        value: filterValue,
        matchMode: matchMode,
        operator: 'and'
      }];
      // this.ccTable._filter();
    } else if (this.ccTable.filters[columnConfig.field]) {
      const filter: FilterMetadata[] = this.ccTable.filters[columnConfig.field] as FilterMetadata[];
      if (filter) {
        let filterValue: FilterMetadata = undefined;
        if (filter['value']) {
          filterValue = filter['value'];
        } else if (filter[0] && filter[0].value) {
          filterValue = filter[0].value;
        }
        if (filterValue) {
          this.ccTable.filters[columnConfig.field] = [{
            value: undefined,
            matchMode: filterValue.matchMode,
            operator: filterValue.operator
          }];
        }
      }
    }

  }

  public setFiltersAndSort(request: TableRequest): void {

    if (request.filters.length == 0) {
      for (let filtersKey in this.ccTable.filters) {
        // delete this.ccTable.filters[filtersKey];
        const filter: FilterMetadata[] = this.ccTable.filters[filtersKey] as FilterMetadata[];
        if (filter) {
          let filterValue: FilterMetadata = undefined;
          if (filter['value']) {
            filterValue = filter['value'];
          } else if (filter[0] && filter[0].value) {
            filterValue = filter[0].value;
          }
          if (filterValue) {
            this.ccTable.filters[filtersKey] = [{
              value: undefined,
              matchMode: filterValue.matchMode,
              operator: filterValue.operator
            }];
          }
        }
      }
    } else {
      request.filters.forEach(filter => {
        if (!this.ccTable.isFilterBlank(filter.value)) {
          this.ccTable.filters[filter.field] = [{
            value: filter.value,
            matchMode: filter.matchMode,
            operator: 'and'
          }];
        } else if (this.ccTable.filters[filter.field]) {
          // delete this.ccTable.filters[filter.field];
          this.ccTable.filters[filter.field] = [{
            value: undefined,
            matchMode: filter.matchMode,
            operator: filter.operator
          }];
        }
        if (filter.field === 'global')
          this.globalFilterValue = request.globalFilter;
      });
    }
    // this.ccTable._filter();

    if (request.globalFilter !== undefined && request.globalFilter !== null && request.globalFilter !== '') {
      this.ccTable.filters['global'] = [{
        value: request.globalFilter,
        matchMode: 'contains',
        operator: 'and'
      }] as any;
      this.globalFilterValue = request.globalFilter;
    } else {
      delete this.ccTable.filters['global'];
      this.globalFilterValue = undefined;
    }

    this.ccTable.sortField = request.sortField;
    this.ccTable.sortOrder = request.sortOrder;

    this.reloadTableValueMap();

  }

  protected hasFilter(): boolean {
    let hasFilter: boolean = false;
    if (this.globalFilterValue)
      hasFilter = true;
    else if (this.ccTable) {
      for (let key in this.ccTable.filters) {
        if (key != 'global') {
          const filter: FilterMetadata[] = this.ccTable.filters[key] as FilterMetadata[];
          if (filter && (filter['value'] || filter[0] && filter[0].value)) {
            hasFilter = true;
            break;
          }
        }
      }
    }
    return hasFilter;
  }

  protected clearFilter() {
    this.globalFilterValue = undefined;

    if (this.ccTable?.filters) {
      Object.keys(this.ccTable.filters).forEach(key => delete this.ccTable.filters[key]);
    }

    const newRequest = this.buildRequestFromUi(true);
    newRequest.globalFilter = undefined;
    newRequest.filters = [];

    this.setFiltersAndSort(newRequest);
    this.loadRecordsFromRequest(newRequest);
    this.onClearFilter.emit(newRequest);
    this.reloadTableValueMap();
  }


  // protected clearSort() {
  //
  //   const globalFilterValue = this.globalFilterValue;
  //   const tableFilters = {...this.ccTable.filters};
  //
  //   setTimeout(() => {
  //     this.ccTable.clear();
  //     this.ccTable.sortField = (this.defaultSortField || this.defaultSortField === '' ? this.defaultSortField : this.fieldName);
  //     this.ccTable.sortOrder = this.defaultSortOrder != undefined ? this.defaultSortOrder : 1;
  //     this.ccTable.sortSingle();
  //     this.ccTable.filters = tableFilters;
  //     this.globalFilterValue = globalFilterValue;
  //     // this.ccTable._filter();
  //     if (globalFilterValue) {
  //       this.ccTable.filterGlobal(globalFilterValue, 'contains');
  //     }
  //   });
  //
  // }
  // protected clearSort() {
  //
  //   const globalFilterValue = this.globalFilterValue;
  //   const tableFilters = {...this.ccTable.filters};
  //
  //   setTimeout(() => {
  //     this.ccTable.clear();
  //     this.ccTable.sortField = (this.defaultSortField || this.defaultSortField === '' ? this.defaultSortField : this.fieldName);
  //     this.ccTable.sortOrder = this.defaultSortOrder != undefined ? this.defaultSortOrder : 1;
  //     this.ccTable.sortSingle();
  //     this.ccTable.filters = tableFilters;
  //     this.globalFilterValue = globalFilterValue;
  //     if (globalFilterValue) {
  //       this.ccTable.filterGlobal(globalFilterValue, 'contains');
  //     }
  //   });
  //
  // }
  protected clearSort() {
    const newRequest = this.buildRequestFromUi(true);
    this.ccTable.clear();
    newRequest.sortField = this.getDefaultSortField();
    newRequest.sortOrder = this.getDefaultSortOrder();

    this.setFiltersAndSort(newRequest);
    this.loadRecordsFromRequest(newRequest);
    this.reloadTableValueMap();
  }

  protected hasSort(): boolean {
    if (this.currentRequest) {
      if (this.currentRequest && this.currentRequest.sortField == null) this.currentRequest.sortField = (this.defaultSortField || this.defaultSortField === '' ? this.defaultSortField : this.fieldName);
      return this.currentRequest && (this.currentRequest.sortOrder != this.defaultSortOrder
        || this.currentRequest.sortField !== (this.defaultSortField || this.defaultSortField === '' ? this.defaultSortField : this.fieldName));
    } else {
      return this.ccTable && (this.ccTable.sortField != this.defaultSortField ||
        this.ccTable.sortOrder != this.defaultSortOrder);
    }
  }

  public exportRecords(type: BookType) {
    const tempReportRecords = [];
    const columnKeys = this.tableConfig.map(value => value.field);
    this.records.forEach(record => {
      const tempNewRecord = {};
      this.flatenObject(record, columnKeys, tempNewRecord);
      tempReportRecords.push(tempNewRecord);
    });
    const worksheet = XLSX.utils.json_to_sheet(tempReportRecords);
    const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
    const excelBuffer: any = XLSX.write(workbook, {bookType: type, type: 'array'});
    this.saveAsExcelFile(excelBuffer,
      this.translateService.instant('page.' + this.transPartKey + '.item_name').toLowerCase() + 's',
      type);
    this.loading = false;
  }

  private flatenObject(record, columnKeys: string[], tempNewRecord: {}) {
    for (let key in record) {
      let value = record[key];
      if (columnKeys.includes(key)) {
        let value = record[key];
        if (value && Array.isArray(value)) {
          tempNewRecord[key] = value.join(', ');
        } else if (value && DateTime.isDateTime(value)) {
          tempNewRecord[key] = value.toFormat('yyyy-MM-dd HH:mm:ss');
        } else if (value && typeof value === 'object') {
          this.flatenObject(value, columnKeys, tempNewRecord);
        } else {
          tempNewRecord[key] = value;
        }
      } else if (value && !Array.isArray(value) && !DateTime.isDateTime(value) && typeof value === 'object') {
        this.flatenObject(value, columnKeys, tempNewRecord);
      }
    }
  }

  protected saveAsExcelFile(buffer: any, fileName: string, type: BookType): void {
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    if (type === 'csv') {
      EXCEL_TYPE = 'text/x-csv';
      EXCEL_EXTENSION = '.csv';
    }
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    this.exportAsService.downloadFromBlob(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  public reloadTableValueMap() {
    this.markForCheck();
    this.ccCells?.forEach(cell => {
      cell.markForCheck();
    });
  }

  selectCell($event: MouseEvent, columnDef: TableColumnConfig, recordValue: any) {
    if (this.disableFilters) return;
    if (!columnDef.notFilterable) {
      this.selectedCellValue = recordValue;
      this.selectedCellColumnConfig = columnDef;
    } else {
      this.selectedCellValue = undefined;
      this.selectedCellColumnConfig = undefined;
    }
  }

  @HostListener('document:keydown.control.alt', ['$event'])
  onFilterCellValueAlt(e) {
    if (this.disableFilters) return;
    if (!this.focused) return;

    if (this.selectedCellValue && this.selectedCellColumnConfig) {
      if (this.currentRequest) {
        const newRequest = new TableRequest(this.currentRequest.originEvent);
        newRequest.first = 0;
        newRequest.rows = this.currentRequest.rows;
        newRequest.sortField = this.currentRequest.sortField;
        newRequest.sortOrder = this.currentRequest.sortOrder;
        newRequest.globalFilter = this.currentRequest.globalFilter;
        newRequest.filters = [...(this.currentRequest.filters ?? [])];

        const existingIndex = newRequest.filters.findIndex(
          value => value.field === this.selectedCellColumnConfig.field
        );

        if (existingIndex >= 0) {
          if (newRequest.filters[existingIndex].value == this.selectedCellValue) {
            newRequest.filters.splice(existingIndex, 1);
          } else {
            newRequest.filters[existingIndex] = {
              ...newRequest.filters[existingIndex],
              value: this.selectedCellValue
            };
          }
        } else {
          newRequest.filters.push({
            field: this.selectedCellColumnConfig.field,
            matchMode: this.selectedCellColumnConfig.filterConfig?.matchMode ?? 'equals',
            operator: 'and',
            value: this.selectedCellValue
          });
        }

        this.setFiltersAndSort(newRequest);
        this.loadRecordsFromRequest(newRequest);

        this.selectedCellValue = undefined;
        this.selectedCellColumnConfig = undefined;
        return;
      }

      this.setFilter(this.selectedCellColumnConfig, this.selectedCellValue);
      this.reloadTableValueMap();
      this.selectedCellValue = undefined;
      this.selectedCellColumnConfig = undefined;
    } else {
      this.clearFilter();
    }

    this.onShCtFilter();
  }

  onShCtFilter() {
  }

  selectKeyUp($event: KeyboardEvent) {
  }

  selectKeyDown($event: KeyboardEvent) {
  }

  @HostListener('document:keydown.ArrowDown', ['$event'])
  @HostListener('document:keydown.shift.ArrowDown', ['$event'])
  rowSelectDownShortCut(e) {
    if (this.disableSelection) return;
    if (!this.focused) return;
    this.selectKeyDown(e);
  }

  @HostListener('document:keydown.ArrowUp', ['$event'])
  @HostListener('document:keydown.shift.ArrowUp', ['$event'])
  rowSelectUpShortCut(e) {
    if (this.disableSelection) return;
    if (!this.focused) return;
    const now = Date.now();
    this.selectKeyUp(e);
  }

  @HostListener('document:keydown.control.shift', ['$event'])
  rowDblClickShortCut(e) {
    if (!this.focused) return;
    this.onCtrlShift.emit();
  }

  @HostListener('document:keydown.shift.s', ['$event'])
  rowShiftSShortCut(e) {
    if (!this.focused) return;
    this.onShift_S.emit();
  }

  @HostListener('document:keyup.control', ['$event'])
  ctrlRepeat(e) {
    if (!this.focused) return;
    const newTime: DateTime = DateTime.now();
    if (newTime.diff(this.ctrlHitLastTime).milliseconds < this.repeatTimeDiff) {
      this.clearFilter();
    }
    this.ctrlHitLastTime = newTime;
  }

  @HostListener('document:keyup.shift', ['$event'])
  shiftRepeat(e) {
    if (!this.focused) return;
    const newTime: DateTime = DateTime.now();
    if (newTime.diff(this.shiftHitLastTime).milliseconds < this.repeatTimeDiff) {
      this.clearSort();
    }
    this.shiftHitLastTime = newTime;
  }

  focuseIn(rowIndex, mouseover = false) {
    const canPass: boolean = (mouseover && AppComponent.focusTableMouseOver)
      || (!mouseover && !AppComponent.focusTableMouseOver);
    if (canPass && !this.focused) {
      this.focused = true;
      if (rowIndex > 0) {
        this.focusedRowIndex = rowIndex;
      } else if (!this.focusedRowIndex || this.focusedRowIndex < 0) {
        this.focusedRowIndex = 0;
      }
    }
  }

  focusOut(mouseover = false) {
    const canPass: boolean = (mouseover && AppComponent.focusTableMouseOver)
      || (!mouseover && !AppComponent.focusTableMouseOver);
    if (canPass) {
      this.focused = false;
    }
  }

  private normalizeRequestFilters(filters: any[] = []): any[] {
    return (filters ?? [])
      .filter(filter =>
        filter &&
        filter.field &&
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== ''
      )
      .map(filter => ({
        field: filter.field,
        matchMode: filter.matchMode ?? 'equals',
        operator: filter.operator ?? 'and',
        value: filter.value
      }));
  }

  protected toPrimeNgFilters(request: TableRequest): { [key: string]: any } {
    const result: { [key: string]: any } = {};

    this.normalizeRequestFilters(request.filters).forEach(filter => {
      result[filter.field] = [{
        value: filter.value,
        matchMode: filter.matchMode,
        operator: filter.operator
      }];
    });

    if (request.globalFilter !== undefined && request.globalFilter !== null && request.globalFilter !== '') {
      result['global'] = [{
        value: request.globalFilter,
        matchMode: 'contains',
        operator: 'and'
      }];
    }

    return result;
  }

  private syncOriginEventFromRequest(request: TableRequest): void {
    request.filters = this.normalizeRequestFilters(request.filters);
    request.first = request.first ?? 0;
    request.rows = request.rows ?? this.currentRequest?.rows ?? this.ccTable?.rows ?? 100;
    request.sortField = request.sortField ?? this.currentRequest?.sortField ?? this.defaultSortField ?? this.fieldName;
    request.sortOrder = request.sortOrder ?? this.currentRequest?.sortOrder ?? this.defaultSortOrder ?? 1;

    request.originEvent = {
      ...(this.currentRequest?.originEvent ?? {}),
      first: request.first,
      rows: request.rows,
      sortField: request.sortField,
      sortOrder: request.sortOrder,
      globalFilter: request.globalFilter,
      // stateFilter: (request as any).stateFilter,
      multiSortMeta: request.multiSortMeta,
      filters: this.toPrimeNgFilters(request)
    };
  }

  private isSameRequest(current: TableRequest, next: TableRequest): boolean {
    return (
      (current?.first ?? 0) === (next?.first ?? 0) &&
      (current?.rows ?? this.ccTable?.rows ?? 100) === (next?.rows ?? this.ccTable?.rows ?? 100) &&
      (current?.sortField ?? this.defaultSortField ?? this.fieldName) === (next?.sortField ?? this.defaultSortField ?? this.fieldName) &&
      (current?.sortOrder ?? this.defaultSortOrder ?? 1) === (next?.sortOrder ?? this.defaultSortOrder ?? 1) &&
      (current?.globalFilter ?? '') === (next?.globalFilter ?? '') &&
      JSON.stringify(this.normalizeRequestFilters(current?.filters)) === JSON.stringify(this.normalizeRequestFilters(next?.filters)) &&
      JSON.stringify(current?.multiSortMeta ?? []) === JSON.stringify(next?.multiSortMeta ?? [])
    );
  }

  protected getDefaultSortField(): string {
    return (this.defaultSortField || this.defaultSortField === '')
      ? this.defaultSortField
      : this.fieldName;
  }

  protected getDefaultSortOrder(): number {
    return this.defaultSortOrder != undefined ? this.defaultSortOrder : 1;
  }

  protected extractFiltersFromTable(): any[] {
    const result: any[] = [];

    if (!this.ccTable?.filters) {
      return result;
    }

    for (const key of Object.keys(this.ccTable.filters)) {
      if (key === 'global') {
        continue;
      }

      const rawFilter: any = this.ccTable.filters[key];
      const filter = Array.isArray(rawFilter) ? rawFilter[0] : rawFilter;
      const value = filter?.value;

      if (value !== undefined && value !== null && value !== '') {
        result.push({
          field: key,
          matchMode: filter?.matchMode ?? 'equals',
          operator: filter?.operator ?? 'and',
          value
        });
      }
    }

    return result;
  }

  protected buildRequestFromUi(resetFirst: boolean = true): TableRequest {
    const newRequest = new TableRequest(this.currentRequest?.originEvent ?? {});

    newRequest.first = resetFirst ? 0 : (this.currentRequest?.first ?? 0);
    newRequest.rows = this.currentRequest?.rows ?? this.ccTable?.rows ?? newRequest.rows ?? 100;
    newRequest.sortField = this.ccTable?.sortField ?? this.currentRequest?.sortField ?? this.getDefaultSortField();
    newRequest.sortOrder = this.ccTable?.sortOrder ?? this.currentRequest?.sortOrder ?? this.getDefaultSortOrder();
    newRequest.globalFilter = this.globalFilterValue;
    newRequest.filters = this.extractFiltersFromTable();
    newRequest.multiSortMeta = this.ccTable?.multiSortMeta ?? this.currentRequest?.multiSortMeta;

    return newRequest;
  }

  protected triggerRemoteRefreshFromUi(resetFirst: boolean = true): TableRequest {
    const newRequest = this.buildRequestFromUi(resetFirst);
    this.setFiltersAndSort(newRequest);
    this.loadRecordsFromRequest(newRequest);
    return newRequest;
  }

  protected updateGlobalFilterState(value: string): void {
    this.globalFilterValue = value;

    if (!this.ccTable.filters) {
      this.ccTable.filters = {};
    }

    if (value !== undefined && value !== null && value !== '') {
      this.ccTable.filters['global'] = [{
        value,
        matchMode: 'contains',
        operator: 'and'
      }] as any;
    } else {
      delete this.ccTable.filters['global'];
    }
  }
}
